import { useCallback, useEffect, useRef, useState } from "react";

// Watches an element's width and returns the matching column count from the
// breakpoints table. Used so widgets respond to their CONTAINER width
// (essential when previewing inside a constrained-width device frame) rather
// than the window width.
//
// Returns [cols, setRef]. Pass setRef as the element's `ref` prop. We use a
// callback ref instead of a useRef-based effect because widgets typically
// return null while proofs are loading — with useRef + useEffect the observer
// would attach once with a null node, then never re-run when the real node
// finally mounts, leaving cols stuck at the fallback.
//
// breakpoints: [{ min: number, cols: number }] in descending min order.
const useContainerColumns = (breakpoints, fallback = 1) => {
  const [cols, setCols] = useState(fallback);
  const observerRef = useRef(null);

  const setRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      const w = node.offsetWidth || node.clientWidth || 0;
      const match = breakpoints.find((b) => w >= b.min);
      setCols(match ? match.cols : breakpoints[breakpoints.length - 1].cols);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(node);
    observerRef.current = ro;
  }, [breakpoints]);

  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);

  return [cols, setRef];
};

export default useContainerColumns;
